import React, { useState, useContext } from 'react'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import { Typography, Button, Steps, notification } from 'antd'
import dayjs from 'dayjs'
import { FormattedMessage, useIntl } from 'react-intl'

import { FlexRow } from '../Components/FlexboxReusables/FlexItems'
import ReviewQuestions from '../Components/CoursePublishPreparation/Steps/ReviewQuestions'
import ReviewCourseSettings from '../Components/CoursePublishPreparation/Steps/ReviewCourseSettings'
import { CourseContext } from '../Components/Course/CourseContext'
import ReviewSummary from '../Components/CoursePublishPreparation/Steps/ReviewSummary'
import FinalModalBeforePublish from '../Components/CoursePublishPreparation/Steps/FinalModalBeforePublish'
import ReviewPurchasePrice from '../Components/CoursePublishPreparation/Steps/ReviewPurchasePrice'

const MIN_EXAM_DURATION = 30

const CoursePublishPreparation = () => {
  const id = parseInt(useParams().id as string)

  const intl = useIntl()

  const { courseContext } = useContext(CourseContext)
  const course = courseContext?.courses.find((course) => course.id === id)

  const [ currentStep, setCurrentStep ] = useState(0)
  const [ startDate, setStartDate ] = useState<dayjs.Dayjs>(dayjs())
  const [ examDuration, setExamDuration ] = useState<number>(MIN_EXAM_DURATION)
  const [ maxTries, setMaxTries ] = useState<number>(3)
  const [ showModal, setShowModal ] = useState<boolean>(false)

  const validatePurchasePrice = () => {
    if (!course?.purchasePrice) {
      notification.error({
        message: intl.formatMessage({ id: 'notification.error' }),
        description: intl.formatMessage({ id: 'notification.error.purchasePriceValidationFailed' }),
        placement: 'bottomRight',
      })

      return false
    }

    return true
  }

  const validateCourseSettings = () => {
    if (!startDate || !examDuration) {
      notification.error({
        message: intl.formatMessage({ id: 'notification.error' }),
        description: intl.formatMessage({ id: 'notification.error.courseSettingsValidationFailed' }),
        placement: 'bottomRight',
      })
      return false
    }

    return true
  }

  const validateAndProceedToNextStep = () => {
    let validationPassed = false

    switch (currentStep) {
      // Participants
      case 0:
        validationPassed = validatePurchasePrice()
        break

      // Questions
      case 1:
        validationPassed = true // 'Questions' component has built-in validation
        break

      // Course settings
      case 2:
        validationPassed = validateCourseSettings()
        break
    }

    if (validationPassed) {
      setCurrentStep(currentStep + 1)
    }
  }

  const steps = [
    {
      title: <FormattedMessage id='generic.price' />,
      description: (
        <Description>
          <FormattedMessage id='generic.setPurchasePrice' />
        </Description>
      ),
      content: course && (
        <ReviewPurchasePrice course={course} />
      ),
    },
    {
      title: <FormattedMessage id='generic.questions' />,
      description: (
        <Description>
          <FormattedMessage id='course.checkQuestions' />
        </Description>
      ),
      content: <ReviewQuestions course={course} />,
    },
    {
      title: <FormattedMessage id='generic.circumstances' />,
      description: (
        <Description>
          <FormattedMessage id='generic.datesAndDuration' />
        </Description>
      ),
      content: (
        <ReviewCourseSettings
          setStartDate={setStartDate}
          setExamDuration={setExamDuration}
          startDate={startDate}
          examDuration={examDuration}
          minimumExamDuration={MIN_EXAM_DURATION}
          maxTries={maxTries}
          setMaxTries={setMaxTries}
        />
      ),
    },
    {
      title: <FormattedMessage id='generic.summary' />,
      content: (
        <ReviewSummary
          course={course}
          startDate={startDate}
          examDuration={examDuration}
        />
      ),
    },
  ]

  const items = steps.map((item) => ({ key: item.title, title: item.title, description: item.description }))

  return (
    <>
      <FlexRow height='3em' justifyContent='center' alignItems='center'>
        <Typography.Title level={4}>
          <FormattedMessage id='course.preparationForPublishing' />
        </Typography.Title>
      </FlexRow>

      <StepsContainer>
        <Steps current={currentStep} items={items} />
        <ContentContainer>
          {steps[currentStep].content}
        </ContentContainer>
        <FlexRow justifyContent='flex-end' margin='2em 0'>
          <FlexRow justifyContent='flex-start'>
            {currentStep > 0 && (
              <Button style={{ margin: '0 8px' }} onClick={() => setCurrentStep(currentStep - 1)}>
                <FormattedMessage id='generic.previous' />
              </Button>
            )}
          </FlexRow>
          <FlexRow justifyContent='flex-end'>
            {currentStep < steps.length - 1 && (
              <Button type="primary" onClick={() => validateAndProceedToNextStep()}>
                <FormattedMessage id='generic.next' />
              </Button>
            )}
            {currentStep === steps.length - 1 && (
              <Button type="primary" onClick={() => setShowModal(true)}>
                <FormattedMessage id='generic.done' />
              </Button>
            )}
          </FlexRow>
        </FlexRow>
      </StepsContainer>
      {showModal && course && (
        <FinalModalBeforePublish
          course={course}
          startDate={startDate}
          examDuration={examDuration}
          maxTries={maxTries}
          showModal={showModal}
          setShowModal={setShowModal}
        />
      )}
    </>
  )
}

export default CoursePublishPreparation

const StepsContainer = styled.div`
  width: 80%;
  margin: 3em auto;
`

const ContentContainer = styled.div`
  margin-top: 2em;
  width: 100%;
  height: 100%;
  border: 1px solid #e9e9e9;
  border-radius: 1em;
`

const Description = styled(Typography.Text)`
  font-size: 0.8em;
  color: grey;
`